import React, { useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { AppSidebar } from '@/components/layout/app-sidebar';
import { SiteHeader } from '@/components/layout/site-header';
import { BottomNav } from '@/components/layout/bottom-nav';
import DashboardRoutes from '../dashboard/routes';
import OffersRoutes from '../offers/routes';
import ContainersRoutes from '../containers/routes';
import BillsRoutes from '../bills/routes';
import MapRoutes from '../tracking-map/routes';
import AiRoutes from '../ai/routes';
import CostsRoutes from '../costs/routes';
import { AuthMiddleware } from '@/middleware/AuthMiddleware';
import { AuthProvider } from '@/contexts/AuthContext';
import { CompanyProvider } from '@/contexts/CompanyContext';
import { DirectionProvider } from '@/contexts/DirectionContext';
import TwoStepConfigure from '../auth/pages/TwoStepConfiguration';
import AccountSettings from '../settings/pages/AccountSettings';
import { Toaster } from '@/components/ui/toaster';
import { SearchPaletteProvider } from '../search/components/SearchPaletteProvider';
import PageTracker from '../auth/components/PageTracker';
import HelpCenter from '../help/pages/HelpCenter';
import ContactPage from '../contact/pages/ContactPage';
import { OnboardingTourProvider } from '../help/tour/OnboardingTourProvider';
import { RouterLinkProvider } from '@/components/wingo/lib/router-link';

const SIDEBAR_COLLAPSED_KEY = 'tls.sidebarCollapsed';

function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return JSON.parse(localStorage.getItem(SIDEBAR_COLLAPSED_KEY) || 'false');
    } catch {
      return false;
    }
  });

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, JSON.stringify(collapsed));
  }, [collapsed]);

  return [collapsed, setCollapsed] as const;
}

const LayoutContent: React.FC = () => {
  const location = useLocation();
  const isAiChatPage = location.pathname.includes('/ai-chat');
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Închide meniul mobile la schimbarea rutei
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className={'app' + (collapsed ? ' sidebar-collapsed' : '')}>
      <AppSidebar
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />
      {mobileOpen && (
        <div className="sidebar-backdrop" onClick={() => setMobileOpen(false)} />
      )}

      <div className="main">
        <SiteHeader
          onToggleSidebar={() => setCollapsed((v) => !v)}
          onOpenMobileSidebar={() => setMobileOpen(true)}
        />
        <main className={'content' + (isAiChatPage ? ' content-flush' : '')}>
          <Routes>
            <Route path="/dashboard/*" element={<DashboardRoutes />} />
            <Route path="/offers/*" element={<OffersRoutes />} />
            <Route path="/containers/*" element={<ContainersRoutes />} />
            <Route path="/bills/*" element={<BillsRoutes />} />
            <Route path="/map/*" element={<MapRoutes />} />
            <Route path="/costs/*" element={<CostsRoutes />} />
            <Route path="/ai-chat/*" element={<AiRoutes />} />
            <Route
              path="/profile"
              element={
                <div className="container mx-auto py-10">
                  <h1 className="text-2xl font-bold mb-4">Setări profil</h1>
                  <p className="text-muted-foreground">
                    Pagina pentru setări profil va fi disponibilă în curând.
                  </p>
                </div>
              }
            />
            <Route path="/two-factor" element={<TwoStepConfigure />} />
            <Route path="/settings" element={<AccountSettings />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </main>
      </div>

      <BottomNav />
    </div>
  );
};

const LayoutAppRoutes: React.FC = () => {
  return (
    <AuthProvider>
      <AuthMiddleware>
        <DirectionProvider>
          <CompanyProvider>
            <SearchPaletteProvider>
              <Toaster />
              <PageTracker />
              <RouterLinkProvider>
                <OnboardingTourProvider>
                  <LayoutContent />
                </OnboardingTourProvider>
              </RouterLinkProvider>
            </SearchPaletteProvider>
          </CompanyProvider>
        </DirectionProvider>
      </AuthMiddleware>
    </AuthProvider>
  );
};

export default LayoutAppRoutes;
